import moment from "moment";
import queryString from "query-string";

const DATE_QUERY_FORMAT = "YYYY-MM-DD";
const STATUSES = new Set(["tutti", "bozza", "inviato", "accettato", "pagato", "rifiutato"]);

const getParam = (value) => Array.isArray(value) ? value[0] : value;

const parseNumber = (value, fallback = null) => {
  const parsed = Number(getParam(value));
  return Number.isFinite(parsed) ? parsed : fallback;
};

const parseDateParam = (value) => {
  const parsed = moment(getParam(value), DATE_QUERY_FORMAT, true);
  return parsed.isValid() ? parsed : null;
};

const toMomentDate = (dateValue) => {
  if (!dateValue) return null;
  if (moment.isMoment(dateValue)) return dateValue.clone();
  if (dateValue instanceof Date) return moment(dateValue);
  if (typeof dateValue === "object" && typeof dateValue.toDate === "function") {
    return moment(dateValue.toDate());
  }

  const parsedDate = moment(dateValue);
  return parsedDate.isValid() ? parsedDate : null;
};

const getLocationParamValues = ({ useCustomLocation, customLocation, userLocation }) => {
  if (useCustomLocation && customLocation) {
    return {
      location: "custom",
      lat: customLocation.lat,
      lng: customLocation.lng,
    };
  }

  if (userLocation) {
    return {
      location: "user",
      lat: userLocation.latitude,
      lng: userLocation.longitude,
    };
  }

  return null;
};

export const parseQuoteFilters = (search) => {
  const params = queryString.parse(search);
  const rawStatus = getParam(params.status);
  const dateFrom = parseDateParam(params.dateFrom);
  const dateTo = parseDateParam(params.dateTo);
  const lat = parseNumber(params.lat);
  const lng = parseNumber(params.lng);
  const hasCoords = lat !== null && lng !== null;
  const locationMode = getParam(params.location) === "custom" ? "custom" : "user";
  const proximityEnabled = getParam(params.proximity) === "1" && hasCoords;
  const subtotal = parseNumber(params.minSubtotal, 0);

  return {
    statusFilter: STATUSES.has(rawStatus) ? rawStatus : "tutti",
    dateRange: dateFrom && dateTo ? [dateFrom, dateTo] : null,
    useDateRange: Boolean(dateFrom && dateTo),
    subtotalMin: subtotal,
    useSubtotalFilter: params.minSubtotal !== undefined,
    useProximityFilter: proximityEnabled,
    proximityRange: parseNumber(params.radius, 30) || 30,
    userLocation: proximityEnabled && locationMode === "user" ? { latitude: lat, longitude: lng } : null,
    customLocation: proximityEnabled && locationMode === "custom" ? { lat, lng } : null,
    useCustomLocation: proximityEnabled && locationMode === "custom",
    showCompetitors: getParam(params.competitors) !== "0",
    showPartners: getParam(params.partners) !== "0",
  };
};

export const serializeQuoteFilters = ({
  statusFilter,
  dateRange,
  useDateRange,
  subtotalMin,
  useSubtotalFilter,
  useProximityFilter,
  proximityRange,
  userLocation,
  customLocation,
  useCustomLocation,
  showCompetitors,
  showPartners,
}) => {
  const params = {};

  if (statusFilter !== "tutti") {
    params.status = statusFilter;
  }

  if (useDateRange && dateRange && dateRange.length === 2) {
    const dateFrom = toMomentDate(dateRange[0]);
    const dateTo = toMomentDate(dateRange[1]);

    if (dateFrom?.isValid() && dateTo?.isValid()) {
      params.dateFrom = dateFrom.format(DATE_QUERY_FORMAT);
      params.dateTo = dateTo.format(DATE_QUERY_FORMAT);
    }
  }

  if (useSubtotalFilter) {
    params.minSubtotal = String(Number(subtotalMin) || 0);
  }

  if (useProximityFilter) {
    const locationValues = getLocationParamValues({ useCustomLocation, customLocation, userLocation });

    if (locationValues) {
      params.proximity = "1";
      params.radius = String(proximityRange || 30);
      params.location = locationValues.location;
      params.lat = Number(locationValues.lat).toFixed(6);
      params.lng = Number(locationValues.lng).toFixed(6);
    }
  }

  if (!showCompetitors) {
    params.competitors = "0";
  }

  if (!showPartners) {
    params.partners = "0";
  }

  return queryString.stringify(params);
};
